<script setup>

const colorMode = useColorMode()
const filterOpen = ref(false)
const selectedFilter = ref([])

const props = defineProps({
  orderOptions: {
    type: Array,
  },
	filterProperties: {
		type: Object || null
	},
	defaultOrder: {
		type: String
	},
	color: {
		type: String,
		default: 'red'
	}
})

const prepareOptions = (items) => {
	let options = []
	for (const [key, value] of Object.entries(items)) {
		options.push({ key, value })
	}

	return options
}

const { $event } = useNuxtApp()
const emit = defineEmits(['update:sort', 'update:filter', 'reset:filter'])

const updateSort = (value) => {
	emit('update:sort', value, selectedFilter.value)
}
const updateFilter = (value, reload = false) => {
	if(selectedFilter.value.find(item => item.parameter === value.parameter)) {
		selectedFilter.value = selectedFilter.value.map(item => {
			if (item.parameter === value.parameter) {
				return { ...item, value: value.value}
			} else {
				return item
			}
		})
	} else {
		selectedFilter.value.push(value)
	}

	if (reload) sendFilter()
}

const sendFilter = () => {
	emit('update:filter', selectedFilter.value)
	filterOpen.value = false
}

const resetFilter = () => {
	$event('resetFilter')
	emit('reset:filter')
	selectedFilter.value = []
	filterOpen.value = false
}

</script>

<template>
	<ClientOnly>
		<div v-click-outside="() => filterOpen = false">
			<div class="relative flex items-center">
				<SelectSimple v-if="orderOptions.length" class="w-full" @click="filterOpen = false" :options="orderOptions" :selectedOption="defaultOrder || orderOptions[0]" @update="updateSort" />

				<div v-if="Object.entries(filterProperties).length" @click="filterOpen = !filterOpen" :class="[orderOptions.length ? 'border-l-0' : 'min-w-full md:min-w-[587px]']" class="relative z-10 dark:text-light text-dark text-sm xl:text-lg border dark:border-light border-dark py-[14px] px-6 cursor-pointer flex justify-between items-center">
					<span class="inline-block mr-3">{{ $t('general.filter') }}</span>
					<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
						<path fill-rule="evenodd" clip-rule="evenodd" d="M19.2753 9.55409C19.7794 9.19608 20.3817 9.00256 21 9C21.6183 9.00256 22.2206 9.19608 22.7247 9.55409C23.2288 9.91209 23.6099 10.4171 23.816 11H28V13H23.816C23.6124 13.5845 23.2318 14.0912 22.7273 14.4498C22.2227 14.8083 21.619 15.0009 21 15.0009C20.381 15.0009 19.7773 14.8083 19.2727 14.4498C18.7682 14.0912 18.3876 13.5845 18.184 13H4V11H18.184C18.3901 10.4171 18.7712 9.91209 19.2753 9.55409ZM20.4444 12.8315C20.6089 12.9414 20.8022 13 21 13C21.2652 13 21.5196 12.8946 21.7071 12.7071C21.8946 12.5196 22 12.2652 22 12C22 11.8022 21.9414 11.6089 21.8315 11.4444C21.7216 11.28 21.5654 11.1518 21.3827 11.0761C21.2 11.0004 20.9989 10.9806 20.8049 11.0192C20.6109 11.0578 20.4327 11.153 20.2929 11.2929C20.153 11.4327 20.0578 11.6109 20.0192 11.8049C19.9806 11.9989 20.0004 12.2 20.0761 12.3827C20.1518 12.5654 20.28 12.7216 20.4444 12.8315ZM9.2753 17.5541C9.77937 17.1961 10.3817 17.0026 11 17C11.6183 17.0026 12.2206 17.1961 12.7247 17.5541C13.2288 17.9121 13.6099 18.4171 13.816 19H28V21H13.816C13.6124 21.5845 13.2318 22.0912 12.7273 22.4498C12.2227 22.8083 11.619 23.0009 11 23.0009C10.381 23.0009 9.77734 22.8083 9.27275 22.4498C8.76815 22.0912 8.38765 21.5845 8.184 21H4V19H8.184C8.39008 18.4171 8.77123 17.9121 9.2753 17.5541ZM10.4444 20.8315C10.6089 20.9414 10.8022 21 11 21C11.2652 21 11.5196 20.8946 11.7071 20.7071C11.8946 20.5196 12 20.2652 12 20C12 19.8022 11.9414 19.6089 11.8315 19.4444C11.7216 19.28 11.5654 19.1518 11.3827 19.0761C11.2 19.0004 10.9989 18.9806 10.8049 19.0192C10.6109 19.0578 10.4327 19.153 10.2929 19.2929C10.153 19.4327 10.0578 19.6109 10.0192 19.8049C9.98063 19.9989 10.0004 20.2 10.0761 20.3827C10.1518 20.5654 10.28 20.7216 10.4444 20.8315Z" :fill="colorMode.preference === 'dark' ? '#F5F5F5' : '#1D1D1B'"/>
					</svg>
				</div>

				<div
					v-if="Object.entries(filterProperties).length"
					class="absolute w-full top-[100%] right-0 border dark:border-light dark:bg-dark border-dark bg-light border-t-0 p-6 xl:p-8"
					:class="[filterOpen ? 'opacity-100 z-20' : 'opacity-0 pointer-events-none']"
				>
					<div v-for="(select, index) in Object.entries(filterProperties)" :key="index" class="mb-4">
						<Select :name="select[0]" :options="prepareOptions(select[1])" @update="updateFilter" />
					</div>

					<Button :disabled="selectedFilter.length === 0" @click="sendFilter" :type="color" class="w-full justify-center">{{ $t('filter.submit') }}</Button>
					<Button v-if="selectedFilter.length > 0" @click="resetFilter" type="transparent" class="w-full justify-center mt-4">{{ $t('filter.reset') }}</Button>
				</div>
			</div>
		</div>
	</ClientOnly>
</template>